// Чат кооператива — tech/03 §8, ADR-0028 (E08-T01, E08-T02). Канал — `coop-<coopId>` (Р-68). Доставка новых сообщений —
// WS `chat.message`, скрытие — `chat.hidden` (§19). Тексты чата в аналитику не попадают.
import { z } from 'zod';
import { BigIdString, IsoTime, PublicId, Uuid7, strict } from '../common.js';
import { endpoint } from '../endpoint.js';

export const ChatChannel = z.string().regex(/^coop-[1-9][0-9]{0,18}$/);
const ChannelParams = strict({ channel: ChatChannel });
const MsgParams = strict({ channel: ChatChannel, id: BigIdString });

export const ChatMessage = strict({
  id: BigIdString,
  channel: ChatChannel,
  author: strict({ publicId: PublicId, displayName: z.string().min(1).max(32), role: z.enum(['chairman', 'deputy', 'member']).nullable() }),
  body: z.string().max(500),
  replyTo: BigIdString.nullable(),
  createdAt: IsoTime,
  /** Скрыто фильтром или модератором: другим не отдаётся, автор видит со своей пометкой. */
  hidden: z.enum(['filter', 'moderator', 'author']).nullable(),
  clientMsgId: Uuid7.optional(),
});
export type ChatMessage = z.infer<typeof ChatMessage>;

export const getChat = endpoint({
  id: 'chat.list', method: 'GET', path: '/chat/{channel}/messages', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: ChannelParams, query: strict({ before: BigIdString.optional(), limit: z.coerce.number().int().min(1).max(50).optional() }),
  response: strict({ items: z.array(ChatMessage), muteUntil: IsoTime.nullable(), tgGroupUrl: z.string().nullable() }), status: 200,
  errors: ['NOT_IN_COOP'], spec: 'tech/03 §8; ADR-0028 §5', task: 'E08-T01',
});

export const postChat = endpoint({
  id: 'chat.post', method: 'POST', path: '/chat/{channel}/messages', auth: true, signed: true, idempotency: 'natural', rate: 'chat',
  params: ChannelParams, body: strict({ body: z.string().min(1).max(500), replyTo: BigIdString.optional(), clientMsgId: Uuid7 }),
  response: ChatMessage, status: 200, errors: ['CHAT_MUTED', 'BODY_INVALID', 'CHAT_REJECTED', 'NOT_IN_COOP'], spec: 'tech/03 §8; ADR-0028 §1', task: 'E08-T01',
});

/** Удалить своё или скрыть чужое (председатель, заместитель). */
export const deleteChat = endpoint({
  id: 'chat.delete', method: 'DELETE', path: '/chat/{channel}/messages/{id}', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: MsgParams, response: strict({ ok: z.literal(true) }), status: 200, errors: ['FORBIDDEN_ROLE', 'NOT_FOUND', 'NOT_IN_COOP'], spec: 'tech/03 §8; ADR-0028 §2', task: 'E08-T02',
});

/** Жалоба: в очередь модерации с 5 сообщениями контекста (ADR-0028 §2). Одна на сообщение от игрока. */
export const reportChat = endpoint({
  id: 'chat.report', method: 'POST', path: '/chat/{channel}/messages/{id}/report', auth: true, signed: true, idempotency: 'natural', rate: 'write',
  params: MsgParams, body: strict({ reason: z.enum(['offensive', 'spam', 'scam', 'other']) }), response: strict({ ok: z.literal(true) }), status: 200,
  errors: ['ALREADY_REPORTED', 'NOT_FOUND', 'NOT_IN_COOP'], spec: 'tech/03 §8; ADR-0028 §2', task: 'E08-T02',
});

/** Мут участника в чате своего кооператива до 24 ч — председатель или заместитель (ADR-0028 §2; Р-68: в tech/03 нет). */
export const muteInChat = endpoint({
  id: 'chat.mute', method: 'POST', path: '/chat/{channel}/mutes', auth: true, signed: true, idempotency: 'state', rate: 'write',
  params: ChannelParams, body: strict({ publicId: PublicId, durationS: z.int().min(60).max(86_400) }),
  response: strict({ muteUntil: IsoTime }), status: 200, errors: ['FORBIDDEN_ROLE', 'NOT_FOUND', 'NOT_IN_COOP'], spec: 'ADR-0028 §2, §3; tech/10 §7', task: 'E08-T02',
});
